import { TEST_API_PREFIX } from './api-prefix';
import type { RunTraceResult } from './langfuse/types';
import type { TestApiError } from './types';

/**
 * `GET /test/trace`, by default from the page itself.
 *
 * Browser-safe: plain `fetch`. Named distinctly from the server-side
 * `fetchRunTrace` in `langfuse/fetch-run-trace.ts` — that one holds a
 * Langfuse secret key and must never reach this bundle; this one only calls
 * the route, with no credentials of its own. A host whose route lives on
 * another origin passes `apiPrefix` and `credentials` (Pro Studio: the admin
 * backend's `/rest/bobcat/test`, behind the session cookie — PSTUDIO-4682).
 */
export class GetRunTraceError extends Error {
  constructor(
    public readonly status: number,
    public readonly detail?: string
  ) {
    super(detail ?? `GET /test/trace failed with ${status}`);
  }
}

export interface GetRunTraceOptions {
  chatId: string;
  /** When the run this trace belongs to started, so the server bounds its
   *  Langfuse query to roughly the right window. */
  sinceMs: number;
  signal?: AbortSignal;
  fetchImpl?: typeof fetch;
  /** Where the Test View routes live; `/test` on this page by default. */
  apiPrefix?: string;
  /** `'include'` when `apiPrefix` is another origin behind a cookie. */
  credentials?: RequestCredentials;
}

export const getRunTrace = async ({
  chatId,
  sinceMs,
  signal,
  fetchImpl = fetch,
  apiPrefix = TEST_API_PREFIX,
  credentials,
}: GetRunTraceOptions): Promise<RunTraceResult> => {
  const query = new URLSearchParams({ chatId, sinceMs: String(sinceMs) });
  const response = await fetchImpl(`${apiPrefix}/trace?${query.toString()}`, {
    signal,
    ...(credentials ? { credentials } : {}),
  });

  const body = await response.json().catch(() => null);

  if (!response.ok) {
    const error = body as TestApiError | null;
    throw new GetRunTraceError(response.status, error?.detail ?? error?.error);
  }

  if (body === null) {
    // response.ok but the body didn't parse as JSON — a truncated response or
    // proxy hiccup, not a genuine "no trace found" result. Surfacing it as a
    // valid empty RunTraceResult would be indistinguishable from that case.
    throw new GetRunTraceError(
      response.status,
      'GET /test/trace returned an unreadable response body'
    );
  }

  return body as RunTraceResult;
};
